import { DropInIcon, DropInSelectedIcon } from "@images/svgIcons/slots/drop-in"
import { MultiSlotsIcon, MultiSlotsSelectedIcon } from "@images/svgIcons/slots/multi-slots"
import { TrialSessionIcon, TrialSessionSelectedIcon } from "@images/svgIcons/slots/trial-session"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import {
  BoxProgrammation,
  BoxProgrammationDefaultSettings,
  BoxProgrammationLight,
} from "@modules/boxProgrammation/boxProgrammationTypes"
import { BoxProgrammationTemplateCalendarEvent } from "@modules/boxProgrammation/components/boxProgrammationTemplateCalendarEvent"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MAX, BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@modules/boxSlot/boxSlotTypes"
import { BoxSlotTemplate, Weekday } from "@modules/boxSlotTemplate/boxSlotTemplateTypes"
import {
  getCancellationBeginningOptions,
  getRegistrationBeginningOptions,
  getRegistrationEndOptions,
} from "@modules/boxSlotTemplate/boxSlotTemplateUtils"
import {
  BoxSlotTemplateFormDeleteModal,
  BoxSlotTemplateFormDeleteModalRef,
} from "@modules/boxSlotTemplate/components/boxSlotTemplateFormDeleteModal"
import {
  BoxSlotTemplateFormModal,
  BoxSlotTemplateFormModalRef,
} from "@modules/boxSlotTemplate/components/boxSlotTemplateFormModal"
import { useBoxSlotTemplateFormik } from "@modules/boxSlotTemplate/hooks/useBoxSlotTemplateFormik"
import { convertBoxSlotTemplateToEvent } from "@modules/boxSlotTemplate/hooks/useBoxSlotTemplates"
import { word } from "@modules/core/utils/i18n"
import { useDateTimeCalendarProps } from "@modules/form/hooks/useDateTimeCalendarProps"
import { AssignedCoachInput } from "@modules/formik/components/assignedCoachInput"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUButton } from "@modules/ui/components/huButton"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { HUTimeDropdownOrCustomizedTimeInput } from "@modules/ui/components/huDropdownWithCustomizedTimeInput"
import { HUText } from "@modules/ui/components/huText"
import { useTimeCharAndMasks } from "@modules/utils/dateUtils"
import { useLocaleWeekDays } from "@modules/utils/hooks/useWeekdays"
import dayjs, { Dayjs } from "dayjs"
import { useMountEffect } from "primereact/hooks"
import { useRef, useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

export const BoxSlotTemplateForm: React.FC<{
  boxProgrammation: BoxProgrammation
  boxSlotTemplate: BoxSlotTemplate | null
  dateToCreateSlotTemplateOn?: Dayjs
  defaultSettings?: BoxProgrammationDefaultSettings | null
}> = ({ boxProgrammation, boxSlotTemplate, dateToCreateSlotTemplateOn, defaultSettings }) => {
  const { hideSidebar } = useSidebar()
  const theme = useTheme()
  const weekDaysOptions = useLocaleWeekDays()
  const { formik, isSubmitting } = useBoxSlotTemplateFormik(
    boxProgrammation,
    boxSlotTemplate,
    dateToCreateSlotTemplateOn,
    defaultSettings,
  )

  const isEdition = !!boxSlotTemplate
  const { startDateTimeMinDate, endDateTimeMinDate, dateTimeFormat, validateStartDateTime, validateEndDateTime } =
    useDateTimeCalendarProps()
  const registrationBeginningOptions = getRegistrationBeginningOptions()
  const registrationEndOptions = getRegistrationEndOptions()
  const cancellationBeginningOptions = getCancellationBeginningOptions()
  const { dayChar, dayMask } = useTimeCharAndMasks()
  const boxSlotTemplateFormModal = useRef<BoxSlotTemplateFormModalRef>(null)
  const boxSlotTemplateFormDeleteModal = useRef<BoxSlotTemplateFormDeleteModalRef>(null)
  const [formikInitialValues] = useState(formik.initialValues)

  const confirmDeleteBoxSlotTemplate = () => {
    boxSlotTemplateFormDeleteModal.current?.open()
  }

  const toggleMultiBooking = () => {
    formik.setFieldValue("allowMultiBooking", !formik.values.allowMultiBooking)
  }

  const toggleDropIn = () => {
    formik.setFieldValue("dropinsAccepted", !formik.values.dropinsAccepted)
  }

  const toggleTrialSessions = () => {
    formik.setFieldValue("trialsAccepted", !formik.values.trialsAccepted)
  }

  const submitForm = async () => {
    const changedKeys = boxSlotTemplateFormModal.current?.changedKeys() ?? []

    if (isEdition) {
      if (changedKeys.length > 0) boxSlotTemplateFormModal.current?.open()
      else hideSidebar()
    } else await handleSubmit()
  }

  const handleSubmit = async () => {
    await formik.submitForm()
  }

  // Make sure we use valid end-date
  useMountEffect(() => {
    validateEndDateTime(
      formik.getFieldProps("endDateTime").value,
      formik.getFieldProps("startDateTime").value,
      (val) => {
        formik.setFieldValue("endDateTime", val)
      },
    )
  })

  return (
    <>
      <div className="flex flex-column gap-3 pb-4">
        <div className="flex  justify-content-between">
          <BoxProgrammationTemplateCalendarEvent
            event={convertBoxSlotTemplateToEvent(
              {
                id: boxSlotTemplate?.id ?? "",
                program: boxProgrammation as BoxProgrammationLight,
                startTime: dayjs(formik.values.startDateTime).format("HH:mm:ss"),
                endTime: dayjs(formik.values.endDateTime).format("HH:mm:ss"),
                weekDay: (formik.values.day !== "" ? formik.values.day : "MONDAY") as Weekday,
                participantLimit: formik.values.participantLimit,
                assignedStaffMembers: formik.values.assignedStaffMembers ?? [],
              },
              theme as DefaultTheme,
              (formik.values.day !== "" ? formik.values.day : "MONDAY") as Weekday,
            )}
            key={boxSlotTemplate?.id ?? ""}
            canBeClicked={false}
            isOverlayed={true}
            relativePosition
          />
          <HUCardAlert
            title={word("programmation.slotTemplates.form.alert.label")}
            icon={<WarningHexagonalIcon width={55} height={55} color={theme.mainWhite} />}
            className="w-8 py-3 border-round-2xl align-items-center"
            titleFontStyle="BM"
          />
        </div>

        <FormikInput
          flex
          formikStyle={{ flexGrow: 1 }}
          isRequiredInput
          label={word("programmation.slotTemplates.form.weekday.label")}
          placeHolder={word("programmation.slotTemplates.form.weekday.placeHolder")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("day", formik)}
          name="day"
          error={getFormErrorMessage("day", formik)}
          setFieldValue={formik.setFieldValue}
          type="dropdown"
          dropdownProps={{
            value: formik.getFieldProps("day").value,
            options: weekDaysOptions,
          }}
        />

        <div className="flex gap-2">
          <FormikInput
            flex
            isRequiredInput
            formikStyle={{ display: "flex", flexGrow: 1 }}
            label={word("programmation.slotTemplates.form.hour.startTime")}
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid(`startDateTime`, formik)}
            error={getFormErrorMessage(`startDateTime`, formik)}
            name={`startDateTime`}
            type="calendarTime"
            calendarProps={{
              minDate: startDateTimeMinDate,
              datetimeformat: dateTimeFormat,
              placeholder: "08:00",
              value: formik.values.startDateTime,
              onChange: (event) => {
                const newValue = event.value?.valueOf()
                if (typeof newValue === "number") {
                  validateStartDateTime(newValue, formik.values.endDateTime, (val) =>
                    formik.setFieldValue(`endDateTime`, val),
                  )
                }
              },
            }}
          />
          <FormikInput
            flex
            isRequiredInput
            formikStyle={{ display: "flex", flexGrow: 1 }}
            label={word("programmation.slotTemplates.form.hour.endTime")}
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid(`endDateTime`, formik)}
            error={getFormErrorMessage(`endDateTime`, formik, {
              errorMessage: word("sessionForm.error.date"),
            })}
            name={`endDateTime`}
            type="calendarTime"
            calendarProps={{
              minDate: endDateTimeMinDate,
              datetimeformat: dateTimeFormat,
              value: formik.values.endDateTime,
              placeholder: "19:00",
              onChange: (event) => {
                const newValue = event.value?.valueOf()
                if (typeof newValue === "number") {
                  validateEndDateTime(
                    newValue,
                    formik.values.startDateTime,
                    (val) => formik.setFieldValue(`startDateTime`, val),
                    true,
                  )
                }
              },
            }}
          />
        </div>

        <FormikInput
          flex
          formikStyle={{ flexGrow: 1 }}
          isRequiredInput
          label={word("programmation.slotTemplates.form.participantLimit.label")}
          getFieldProps={formik.getFieldProps}
          isInvalid={isFormFieldInValid("participantLimit", formik)}
          name="participantLimit"
          error={getFormErrorMessage("participantLimit", formik, {
            errorMessage: word("global.form.required"),
          })}
          setFieldValue={formik.setFieldValue}
          type="number"
          numberProps={{
            min: BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
            max: BOX_SLOT_PARTICIPANTS_LIMIT_MAX,
            showButtons: true,
            maxFractionDigits: 0,
          }}
        />

        <div className="flex flex-row justify-content-between align-items-center gap-2">
          {/* Drop in accepted */}
          <div
            className={`flex flex-column align-items-center justify-content-between w-full gap-2 py-3 px-2 align-self-stretch border-round border-solid cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.dropinsAccepted ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
            onClick={toggleDropIn}
          >
            {formik.values.dropinsAccepted ? <DropInSelectedIcon color="" /> : <DropInIcon color="" />}
            <HUText
              fontStyle="TS"
              className="text-center"
              color={formik.values.dropinsAccepted ? theme.mainWhite : theme.neutralColor600}
            >
              {formik.values.dropinsAccepted
                ? word("box.slotTemplateGroup.form.dropIn.allowed")
                : word("box.slotTemplateGroup.form.dropIn.rejected")}
            </HUText>
          </div>

          {/* Trial session accepted*/}
          <div
            className={`flex flex-column align-items-center justify-content-between w-full gap-2 py-3 px-2 align-self-stretch border-round border-solid  cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.trialsAccepted ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
            onClick={toggleTrialSessions}
          >
            {formik.values.trialsAccepted ? <TrialSessionSelectedIcon color="" /> : <TrialSessionIcon color="" />}
            <HUText
              fontStyle="TS"
              className="text-center"
              color={formik.values.trialsAccepted ? theme.mainWhite : theme.neutralColor600}
            >
              {formik.values.trialsAccepted
                ? word("box.slotTemplateGroup.form.trialSession.allowed")
                : word("box.slotTemplateGroup.form.trialSession.rejected")}
            </HUText>
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <HUText type="label" fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.registration.label")}
          </HUText>
          <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.registration.description")}</HUText>
          <div className="flex gap-2">
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitStart"
              dropdownOptions={registrationBeginningOptions}
              getFieldProps={() => formik.getFieldProps("limitStart")}
              label={word("box.slotTemplateGroup.form.registration.beginning")}
              setFieldValue={formik.setFieldValue}
              isInvalid={isFormFieldInValid(`limitStart`, formik)}
              error={getFormErrorMessage(`limitStart`, formik)}
              maskedInputProps={{
                placeholder: dayChar,
                slotChar: dayChar,
                mask: dayMask,
                onChange: (e) => {
                  formik.setFieldValue("limitStart", e.target.value)
                },
              }}
            />
            <HUTimeDropdownOrCustomizedTimeInput
              name="limitEnd"
              dropdownOptions={registrationEndOptions}
              label={word("box.slotTemplateGroup.form.registration.end")}
              getFieldProps={() => formik.getFieldProps("limitEnd")}
              setFieldValue={formik.setFieldValue}
              isInvalid={isFormFieldInValid(`limitEnd`, formik)}
              error={getFormErrorMessage(`limitEnd`, formik)}
              maskedInputProps={{
                placeholder: dayChar,
                slotChar: dayChar,
                mask: dayMask,
                onChange: (e) => {
                  formik.setFieldValue("limitEnd", e.target.value)
                },
              }}
            />
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <HUText type="label" fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.cancellation.label")}
            <HUText fontStyle="LL" color={theme.necessaryInput}>
              {" *"}
            </HUText>
          </HUText>
          <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.cancellation.description")}</HUText>
          <HUTimeDropdownOrCustomizedTimeInput
            name="limitCancel"
            dropdownOptions={cancellationBeginningOptions}
            label={word("box.slotTemplateGroup.form.cancellation.beginning")}
            getFieldProps={() => formik.getFieldProps("limitCancel")}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid(`limitCancel`, formik)}
            error={getFormErrorMessage(`limitCancel`, formik)}
            maskedInputProps={{
              placeholder: dayChar,
              slotChar: dayChar,
              mask: dayMask,
              onChange: (e) => {
                formik.setFieldValue("limitCancel", e.target.value)
              },
            }}
          />
        </div>

        <div
          className={`flex w-full gap-4 border-round border-solid p-4 cursor-pointer transition-duration-100 transition-all align-items-center ${formik.values.allowMultiBooking ? "border-1 border-primary" : "border-1 border-300  hover:border-500"}`}
          onClick={toggleMultiBooking}
        >
          <div className="flex flex-column w-9 gap-3 align-items-center ">
            {formik.values.allowMultiBooking ? <MultiSlotsSelectedIcon color="" /> : <MultiSlotsIcon color="" />}
            <HUText
              fontStyle="TS"
              className="text-center"
              color={formik.values.allowMultiBooking ? theme.mainWhite : theme.neutralColor600}
            >
              {formik.values.allowMultiBooking
                ? word("box.slotTemplateGroup.form.mutliSlotBooking.allowed")
                : word("box.slotTemplateGroup.form.mutliSlotBooking.rejected")}
            </HUText>
          </div>
          <HUText fontStyle="BS" color={theme.neutralColor600} style={{ whiteSpace: "pre-line" }}>
            {word("box.slotTemplateGroup.form.mutliSlotBooking.description")}
          </HUText>
        </div>

        <div className="flex flex-column gap-2">
          <HUText type="label" fontStyle="LS" className="uppercase">
            {word("box.slotTemplateGroup.form.area.title")}
          </HUText>
          <HUText fontStyle="BS">{word("box.slotTemplateGroup.form.area.subtitle")}</HUText>
          <FormikInput
            flex
            formikStyle={{ display: "flex", flexGrow: 1 }}
            name="location"
            isRequiredInput
            getFieldProps={formik.getFieldProps}
            isInvalid={isFormFieldInValid("location", formik)}
            setFieldValue={formik.setFieldValue}
            error={getFormErrorMessage("location", formik)}
            placeHolder={word("box.slotTemplateGroup.form.area.placeholder")}
          />
        </div>

        <div className="flex flex-column">
          <AssignedCoachInput
            label={word("box.slotTemplateGroup.form.assignedStaffMembers.label")}
            textDescription={word("box.slotTemplateGroup.form.assignedStaffMembers.description")}
            name="assignedStaffMembers"
            isRequiredInput={false}
            isInvalid={isFormFieldInValid("assignedStaffMembers", formik)}
            setFieldValue={formik.setFieldValue}
            getFieldProps={formik.getFieldProps}
            error={getFormErrorMessage("assignedStaffMembers", formik)}
          />
        </div>

        <div className="flex justify-content-end mt-3">
          <div className="flex column gap-3 sm:flex-row flex-grow-1">
            {isEdition ? (
              <HUButton
                type="Default"
                size="M"
                colorType="Quaternary"
                text={word("global.delete")}
                onClick={() => confirmDeleteBoxSlotTemplate()}
                className="w-full"
              />
            ) : (
              <HUButton
                type="Default"
                size="M"
                colorType="Quaternary"
                text={word("global.cancel")}
                onClick={() => hideSidebar()}
                className="w-full"
              />
            )}
            <HUButton
              type="Default"
              size="M"
              colorType="Primary"
              text={word("box.slot.form.submit.button")}
              className="mr-2 w-full"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={submitForm}
            />
          </div>
        </div>
      </div>
      <BoxSlotTemplateFormModal
        formik={formik}
        formikInitialValues={formikInitialValues}
        handleSubmit={handleSubmit}
        ref={boxSlotTemplateFormModal}
      />
      {boxSlotTemplate && (
        <BoxSlotTemplateFormDeleteModal
          boxProgrammation={boxProgrammation}
          boxSlotTemplate={boxSlotTemplate}
          ref={boxSlotTemplateFormDeleteModal}
        />
      )}
    </>
  )
}
